import * as React from "react";
import { Button, Stack, Typography } from "@mui/material";
import { DeleteModal, CustomeDialog, CustomTable } from "../../../shared";
import { useParams } from "react-router-dom";
import { sortItems } from "../../../utils";
import { useStampCardCustomers } from "../hooks";
import { StampCardCustomersDialog } from "./StampCardCustomersDialog";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";

export const StampCardCustomersList = () => {
  const { id } = useParams();
  const {
    state,
    isDeletable,
    isEditable,
    DeleteModals,
    handleDeleteCustomer,
    toggleSelectAll,
    applyToAllCustomers,
    handleApplyAllConfirmModal,
    openAssignModal,
    closeAssignModal,
    handleUsersSelect,
    isSelectedUser,
    loadNextPage,
    HandleFilterUsers,
    handleAssignCustomer,
  } = useStampCardCustomers({
    stampCardId: id,
  });

  const ActionButtons = ({ row, deleteAction }) => {
    return (
      <div className="action-buttons" onClick={(e) => e.preventDefault()}>
        <img
          alt="delete"
          onClick={deleteAction}
          src="/icons/ic_delete.svg"
          style={{
            cursor: "pointer",
            opacity: 1,
          }}
        />
      </div>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Phone",
        accessor: "phone",
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        disableGlobalFilter: true,
        accessor: (row) => {
          return (
            <ActionButtons
              row={row}
              deleteAction={() => {
                DeleteModals(true, row.id);
              }}
            />
          );
        },
      },
    ],
    [isDeletable],
  );

  const isRemovingLastCustomer =
    state.isDelete && (state.customers?.length || 0) === 1;

  return (
    <>
      {state.customers?.length ? (
        <CustomTable
          columns={columns}
          data={state.customers || []}
          isLoading={state.isBusy}
          searchBar={true}
          defaultSearch={true}
          tableTitle="Customers"
          bulkDelete={false}
          emptyMessage={
            state.searchText
              ? "No matches found"
              : "It is applicable for all customers"
          }
          headerRightContent={
            <Stack direction="row" spacing={2}>
              <Button
                type="button"
                size="medium"
                variant="text"
                onClick={() => handleApplyAllConfirmModal(true)}
              >
                Apply To All Customers
              </Button>
              <Button
                type="button"
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAssignModal}
              >
                Add/Modify Customers
              </Button>
            </Stack>
          }
        />
      ) : (
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            border: "2px Dashed #D1D5DC",
            borderRadius: "12px",
            padding: "16px",
            my: "32px",
          }}
        >
          <Stack
            sx={{
              borderRadius: "50px",
              minWidth: "40px",
              height: "40px",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#E0E7FF",
            }}
          >
            <PeopleIcon color="primary" />
          </Stack>
          <Stack sx={{ width: "100%", gap: "4px" }}>
            <Typography
              fontWeight="bold"
              fontSize="18px"
              lineHeight="20px"
              color="#111111"
            >
              Available to all customers
            </Typography>
            <Typography fontWeight={400} fontSize="14px" color="#636363">
              This stamp card is available to all customers. You can restrict
              access by selecting specific customers.
            </Typography>
          </Stack>
          <Button
            type="button"
            size="medium"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ minWidth: "220px" }}
            onClick={openAssignModal}
          >
            Select Customers
          </Button>
        </Stack>
      )}
      <DeleteModal
        open={state.isDelete}
        handleClose={() => DeleteModals(false)}
        onConfirm={handleDeleteCustomer}
        isDeleteButton={state.isBusy}
        title={
          isRemovingLastCustomer ? "Apply To All Customers" : "Delete Customer"
        }
        description={
          isRemovingLastCustomer
            ? "You are about to remove the last customer. This will make this stamp card applicable to all customers. Are you sure you want to continue?"
            : "Are you sure you want to remove this customer from the stamp card?"
        }
        confirmText={isRemovingLastCustomer ? "Confirm" : "Delete"}
        confirmColor={isRemovingLastCustomer ? "primary" : "error"}
      />

      <CustomeDialog
        open={state.isApplyAllConfirmOpen}
        PaperProps={{
          sx: { width: "423px", p: "16px" },
        }}
        title="Apply To All Customers"
        content={
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will make this stamp card applicable to all customers. Are you sure you want to continue?
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                type="button"
                size="medium"
                onClick={() => handleApplyAllConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="medium"
                variant="contained"
                onClick={applyToAllCustomers}
                disabled={state.isBusy}
              >
                Confirm
              </Button>
            </Stack>
          </Stack>
        }
      />

      <CustomeDialog
        open={state.assign.open}
        handleClose={closeAssignModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { width: "423px", height: "535px", p: "16px" },
        }}
        title="Customers"
        content={
          <StampCardCustomersDialog
            state={state.assign}
            handleSelect={handleUsersSelect}
            isSelectedUser={isSelectedUser}
            handleChange={handleAssignCustomer}
            disabled={
              !state.assign.selectAll && !state.assign.selectedUsers.length
            }
            loadNextPage={loadNextPage}
            hasMorePages={state.assign.pagination.hasMorePages}
            HandleFilter={HandleFilterUsers}
          />
        }
      />
    </>
  );
};
